import type { ProductContext } from "../types";
import { extractSlugPathFromUrl, findProductJsonLd, hasProductJsonLd } from "./json-ld";
import type { PlatformAdapter } from "./types";

declare global {
  interface Window {
    vtex?: unknown;
    // Confirmado em loja VTEX real: pageContext.type é sempre "route" (genérico,
    // não diferencia tipo de página). Quem identifica a página é route.id
    // (ex: "store.home" na home — numa PDP deve ser algo como "store.product",
    // ainda não confirmado contra uma página de produto real).
    __RUNTIME__?: {
      route?: {
        id?: string;
        path?: string;
        pageContext?: { id?: string; type?: string };
      };
    };
  }
}

function matchesProductRoute(): boolean {
  const id = window.__RUNTIME__?.route?.id ?? window.__RUNTIME__?.route?.pageContext?.id;
  return typeof id === "string" && id.toLowerCase().includes("product");
}

function matchesProductPath(): boolean {
  const segments = window.location.pathname.split("/").filter(Boolean);
  return segments.length > 1 && segments[segments.length - 1]?.toLowerCase() === "p";
}

function extractSlugFromPath(path?: string): string | null {
  if (!path) return null;
  const segments = path.split("/").filter(Boolean);
  const last = segments[segments.length - 1];
  return last ?? null;
}

export const vtexAdapter: PlatformAdapter = {
  isCurrentPlatform() {
    return typeof window.vtex !== "undefined" || typeof window.__RUNTIME__ !== "undefined";
  },

  isProductPage() {
    // Em algumas lojas VTEX o structured-data só publica o Product após a hidratação.
    // Como o runtime global também pode não estar disponível, reconhecemos o padrão
    // nativo de PDP (/<slug>/p) para que o handler continue tentando durante o carregamento.
    return matchesProductRoute() || matchesProductPath() || hasProductJsonLd();
  },

  getProductContext(): ProductContext {
    // VTEX instala por padrão o app vtex.structured-data, que publica um
    // JSON-LD @type:Product na PDP — fonte confiável entre temas/contas.
    const fromJsonLd = findProductJsonLd();
    if (fromJsonLd) return fromJsonLd;

    const slug = extractSlugFromPath(window.__RUNTIME__?.route?.path ?? window.location.pathname);
    // slugPath vem da URL real: route.path pode ser um template ("/:slug/p").
    const slugPath = extractSlugPathFromUrl(window.location.href);
    return { productId: null, sku: null, gtin: null, slug, slugPath };
  },
};
